import { useState, useEffect } from 'react';
import { analyzePortfolio } from '../../api/ai';
import { listProjects } from '../../api/projects';
import type { PortfolioAnalysisResponse } from '../../api/ai';
import { useTranslation } from 'react-i18next';
import styles from './PortfolioDashboard.module.css';

function fmtCHF(value: number): string {
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M CHF`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(0)}k CHF`;
  return `${value} CHF`;
}

export function PortfolioDashboard() {
  const { t } = useTranslation(['ai', 'common']);
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [result, setResult] = useState<PortfolioAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    listProjects()
      .then((data) => setProjects(data.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {})
      .finally(() => setProjectsLoading(false));
  }, []);

  function toggleProject(id: string) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  }

  async function handleAnalyze() {
    if (selectedIds.length < 2) return;
    setLoading(true);
    try {
      const res = await analyzePortfolio({ project_ids: selectedIds });
      setResult(res);
    } catch {
      // handle error
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <h2 className={styles.title}>{t('ai:portfolioTitle')}</h2>
      <p className={styles.subtitle}>
        {t('ai:portfolioSubtitle')}
      </p>

      <div className={styles.projectSelect}>
        <span className={styles.projectSelectLabel}>{t('ai:selectProjects')}</span>
        <div className={styles.projectList}>
          {projectsLoading ? (
            <div style={{ padding: 12, color: 'var(--color-text-muted)', fontSize: 13 }}>{t('common:loadingProjects')}</div>
          ) : projects.length === 0 ? (
            <div style={{ padding: 12, color: 'var(--color-text-muted)', fontSize: 13 }}>{t('common:noProjects')}</div>
          ) : (
            projects.map((p) => (
              <label key={p.id} className={styles.projectItem}>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(p.id)}
                  onChange={() => toggleProject(p.id)}
                />
                {p.name}
              </label>
            ))
          )}
        </div>
      </div>

      <button
        className={styles.analyzeBtn}
        onClick={handleAnalyze}
        disabled={loading || selectedIds.length < 2}
      >
        {loading ? (
          <><span className={styles.spinner} />{t('ai:analyzing')}</>
        ) : (
          <><span>&#x1F4BC;</span> {t('ai:analyzePortfolio', { count: selectedIds.length })}</>
        )}
      </button>

      {result && (
        <>
          {/* Savings highlight */}
          <div className={styles.savingsCard}>
            <div className={styles.savingsLabel}>{t('ai:totalSavings')}</div>
            <div className={styles.savingsValue}>{fmtCHF(result.total_potential_savings)}</div>
          </div>

          {/* Shared Infrastructure */}
          {result.shared_infrastructure.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>{t('ai:sharedInfra')}</h3>
              <div className={styles.infraCards}>
                {result.shared_infrastructure.map((item, i) => (
                  <div key={i} className={styles.infraCard}>
                    <div className={styles.infraType}>{item.facility_type}</div>
                    <div className={styles.infraProjects}>
                      Projects: {item.projects.join(', ')}
                    </div>
                    <div className={styles.infraSaving}>
                      Potential saving: {fmtCHF(item.potential_saving_chf)}
                    </div>
                    <p className={styles.infraExplanation}>{item.explanation}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Phasing Recommendations */}
          {result.phasing_recommendations.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>{t('ai:phasingRecommendations')}</h3>
              <div className={styles.recommendations}>
                {result.phasing_recommendations.map((rec, i) => (
                  <div key={i} className={styles.recItem}>
                    <strong>{String(rec.project || rec.phase || `Phase ${i + 1}`)}</strong>
                    {': '}
                    {String(rec.recommendation || rec.description || '')}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Narrative */}
          {result.narrative && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>{t('ai:analysisSummary')}</h3>
              <div className={styles.narrative}>
                {result.narrative.split('\n').map((line, i) => (
                  <p key={i}>{line}</p>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
